/**
 * Contract Overview Tab
 *
 * Displays contract overview information
 */

import type { ContractResponse } from '@monorepo/shared/schemas/contracts'
import { Calendar, CreditCard, FileText, User } from 'lucide-react'

interface OverviewTabProps {
  contract: ContractResponse
}

export function OverviewTab({ contract }: OverviewTabProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const infoSections = [
    {
      title: 'Customer Information',
      icon: User,
      items: [
        {
          label: 'Customer Name',
          value: contract.customer?.name || '-',
        },
        {
          label: 'Customer Email',
          value: contract.customer?.email || '-',
        },
      ],
    },
    {
      title: 'Plan Information',
      icon: FileText,
      items: [
        {
          label: 'Plan Name',
          value: contract.plan?.name || '-',
        },
        {
          label: 'Plan Description',
          value: contract.plan?.description || '-',
        },
      ],
    },
    {
      title: 'Billing Information',
      icon: CreditCard,
      items: [
        {
          label: 'Billing Interval',
          value:
            contract.billing_interval.charAt(0).toUpperCase() +
            contract.billing_interval.slice(1),
        },
        {
          label: 'Currency',
          value: contract.currency,
        },
        {
          label: 'Collection Method',
          value: contract.collection_method
            ?.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
        },
        {
          label: 'Payment Terms',
          value: contract.payment_terms
            ? `Net ${contract.payment_terms} days`
            : '-',
        },
      ],
    },
    {
      title: 'Contract Dates',
      icon: Calendar,
      items: [
        {
          label: 'Start Date',
          value: formatDate(contract.start_date),
        },
        {
          label: 'End Date',
          value: contract.end_date ? formatDate(contract.end_date) : 'Forever',
        },
        {
          label: 'Signed At',
          value: contract.signed_at ? formatDate(contract.signed_at) : '-',
        },
        {
          label: 'Activated At',
          value: contract.activated_at
            ? formatDate(contract.activated_at)
            : '-',
        },
        {
          label: 'Cancelled At',
          value: contract.canceled_at ? formatDate(contract.canceled_at) : '-',
        },
      ],
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Contract Dates Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {infoSections.map(section => {
          const Icon = section.icon
          return (
            <div key={section.title} className='space-y-3'>
              <div className='flex items-center gap-2'>
                <Icon className='w-4 h-4 text-muted-foreground' />
                <h3 className='font-medium'>{section.title}</h3>
              </div>
              <div className='space-y-2 pl-6'>
                {section.items.map(item => (
                  <div
                    key={item.label}
                    className='flex justify-between items-start'
                  >
                    <span className='text-sm text-muted-foreground'>
                      {item.label}
                    </span>
                    <span className='text-sm font-medium'>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Trial Information */}
      {(contract.trial_start || contract.trial_end) && (
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <h3 className='font-medium text-blue-900 mb-2'>Trial Period</h3>
          <div className='space-y-1 text-sm text-blue-800'>
            {contract.trial_start && (
              <div className='flex justify-between'>
                <span>Trial Start:</span>
                <span className='font-medium'>
                  {formatDate(contract.trial_start)}
                </span>
              </div>
            )}
            {contract.trial_end && (
              <div className='flex justify-between'>
                <span>Trial End:</span>
                <span className='font-medium'>
                  {formatDate(contract.trial_end)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metadata */}
      {contract.metadata && Object.keys(contract.metadata).length > 0 && (
        <div className='space-y-3'>
          <h3 className='font-medium'>Additional Information</h3>
          <div className='bg-muted/50 rounded-lg p-4'>
            <pre className='text-xs overflow-auto'>
              {JSON.stringify(contract.metadata, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}
